import { ethers } from 'ethers';
import { getChainForRequest } from '../config/chains';
import { decryptRandomness } from './ibeDecryption';
import { decryptElGamalWithRandomness } from './elgamalDecryption';
import { ipfsGatewayUrlForCid } from './ipfs';

const normalizeAddr = (addr) => {
  if (!addr || typeof addr !== 'string') return '';
  const s = addr.trim().toLowerCase();
  return s.startsWith('0x') ? s : `0x${s}`;
};

const getChainKeyForTx = (tx) => {
  const chain = getChainForRequest(tx);
  return chain ? chain.id : 'ethereum';
};

// arb-fork node URL (Fairblock). Overridable via env; hardcoded default mirrors the pattern
// used for the chains' own rpcUrl defaults so it works without an env-var reload.
const ARBITRUM_FORK_RPC_URL =
  process.env.REACT_APP_ARBITRUM_FORK_RPC_URL || 'https://arb-fork.fairblock.network';

/**
 * Detect a "fork" transaction (e.g. arb-fork). Fork txs share the base chain's numeric chainId
 * (so they resolve to the same chain config for display/filtering) but live on a separate fork
 * node. The fork marker can appear in the request's chain key/network field, or — for composite
 * ids of the form `<chainId>:<chainKey>:<...>` — inside the id. We treat the tx as a fork tx if
 * "fork" appears in any of those signals.
 * @param {Object} tx
 * @returns {boolean}
 */
const isForkTransaction = (tx) => {
  const candidates = [tx?.chainKey, tx?.chain_key, tx?.chain, tx?.network, tx?.id];
  return candidates.some(
    (c) => typeof c === 'string' && c.toLowerCase().includes('fork')
  );
};

/**
 * Resolve the RPC URL to use for fetching a transaction's calldata. Fork transactions are routed
 * to the chain's fork RPC, so real chain txs and fork txs can both be decrypted without
 * cross-querying the wrong node (they share the same numeric chainId).
 * @param {Object} chain - resolved chain config
 * @param {Object} tx - transaction
 * @returns {string} rpc url
 */
const getRpcUrlForTx = (chain, tx) => {
  if (chain.id === 'arbitrum' && isForkTransaction(tx)) {
    return ARBITRUM_FORK_RPC_URL;
  }
  return chain.rpcUrl;
};

/**
 * Static network for StaticJsonRpcProvider so ethers never runs detectNetwork() (which does
 * BigNumber.from(eth_chainId).toNumber() — Tempo can return a chainId that overflows JS), and
 * never validates our declared chainId against the node's reported one (a fork such as
 * "arb-fork" reports a chainId that differs from real Arbitrum Sepolia).
 *
 * The network name must NOT collide with ethers' built-in network registry (e.g. "arbitrum" =
 * 42161, "bnb" = 56). getNetwork() throws "network chainId mismatch" when a known name is paired
 * with a different chainId, so we namespace the name to guarantee it is treated as a custom
 * network.
 */
const staticNetworkForProvider = (chain) => {
  let chainIdNum = null;
  const id = chain.chainIdDecimal;
  if (typeof id === 'number' && !Number.isNaN(id) && id > 0) {
    chainIdNum = id;
  } else if (typeof chain.chainId === 'string' && chain.chainId.startsWith('0x')) {
    try {
      const n = ethers.BigNumber.from(chain.chainId).toNumber();
      if (!Number.isNaN(n) && n > 0) chainIdNum = n;
    } catch {
      // ignore
    }
  }
  if (chainIdNum == null) return null;
  return { name: `confidential-${chain.id || chainIdNum}`, chainId: chainIdNum };
};

/** How long to wait for eth_getTransactionByHash before failing (ethers default is 120s). */
const RPC_FETCH_TIMEOUT_MS = 20_000;

/**
 * Fetch calldata via raw JSON-RPC only. Do not go through ethers' transaction formatter —
 * on chains like Tempo, fields (nonce / transactionIndex / chainId / type) can exceed
 * Number.MAX_SAFE_INTEGER and ethers throws NUMERIC_FAULT (overflow) in `.toNumber()`.
 *
 * @param {ethers.providers.JsonRpcProvider} provider
 * @param {string} txHash
 * @param {{ chainName?: string, rpcUrl?: string }} [meta]
 * @returns {Promise<string>} Hex calldata (0x…)
 */
const getTransactionCalldata = async (provider, txHash, meta = {}) => {
  const hash =
    typeof txHash === 'string' && txHash.startsWith('0x')
      ? txHash
      : `0x${String(txHash || '').replace(/^0x/i, '')}`;

  let raw;
  try {
    raw = await provider.send('eth_getTransactionByHash', [hash]);
  } catch (err) {
    const msg = err?.message || String(err);
    const timedOut = /timeout|timed out|ETIMEOUT|AbortError/i.test(msg);
    const chainLabel = meta.chainName || 'chain';
    const rpcHint = meta.rpcUrl ? ` (${meta.rpcUrl})` : '';
    if (timedOut) {
      const envHint =
        /stable/i.test(String(meta.chainName || meta.rpcUrl || ''))
          ? ' Set REACT_APP_STABLE_RPC_URL to a reachable endpoint if needed.'
          : '';
      throw new Error(
        `Timed out fetching transaction from ${chainLabel} RPC${rpcHint}.${envHint}`
      );
    }
    throw new Error(`Failed to fetch transaction from ${chainLabel} RPC${rpcHint}: ${msg}`);
  }
  if (raw == null) {
    throw new Error('Transaction not found');
  }
  const data = raw.input ?? raw.data;
  if (!data || data === '0x') {
    console.warn('[transactionDecryption] RPC response missing calldata:', raw);
    throw new Error('Transaction is missing calldata');
  }
  return data;
};

/**
 * Normalize `proof` from calldata to hex (all non-Tempo chains: proof is inline ABI bytes).
 *
 * @param {string | Uint8Array} proofArg
 * @returns {string} hex-encoded ABI bytes (0x…)
 */
const proofArgToHex = (proofArg) => {
  if (!proofArg) {
    throw new Error('Missing transfer proof bytes');
  }
  return typeof proofArg === 'string' ? proofArg : ethers.utils.hexlify(proofArg);
};

/**
 * Tempo only: `proof` may be a UTF-8 `ipfs://<cid>` pointer; fetch the real
 * ABI-encoded (string, string, string) bundle from IPFS. Other chains never call this.
 *
 * @param {string | Uint8Array} proofArg
 * @returns {Promise<string>} hex-encoded ABI bytes (0x…)
 */
const resolveTempoProofFromIpfs = async (proofArg) => {
  const rawHex = proofArgToHex(proofArg);

  try {
    const pointer = ethers.utils.toUtf8String(rawHex).trim();
    if (pointer.startsWith('ipfs://')) {
      const cid = pointer.replace('ipfs://', '').trim();
      if (!cid) {
        throw new Error('Invalid IPFS proof pointer');
      }

      const url = ipfsGatewayUrlForCid(cid);
      console.log('[transactionDecryption] Tempo: fetching proof from IPFS:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch proof from IPFS (${response.status})`);
      }

      const proofBuffer = await response.arrayBuffer();
      return ethers.utils.hexlify(new Uint8Array(proofBuffer));
    }
  } catch (e) {
    if (e && /IPFS|Invalid IPFS/i.test(e.message || '')) {
      throw e;
    }
    // Not an ipfs:// pointer; use calldata proof bytes as-is (unusual on Tempo).
  }

  return rawHex;
};

/**
 * Compute role (sender vs recipient) for a transaction from the viewing user's perspective.
 */
export const getRoleForUser = (tx, userAddress) => {
  const viewingUser = normalizeAddr(userAddress);
  const sender =
    tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
      ? (tx.details?.sender || '')
      : (tx.userAddress || tx.details?.sender || '');
  const receiver =
    tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
      ? (tx.userAddress || tx.details?.recipient || '')
      : (tx.details?.recipient || '');
  const fromNorm = normalizeAddr(sender);
  const toNorm = normalizeAddr(receiver);
  if (fromNorm === viewingUser) return 'sender';
  if (toNorm === viewingUser) return 'recipient';
  return tx.role || 'sender';
};

/**
 * Legacy IBE identity used before identities were stored as address+pk on the request.
 * @param {Object} tx
 * @param {string} userAddress
 * @returns {string|null}
 */
export const getLegacyIbeIdentity = (tx, userAddress) => {
  const addressForDecrypt = tx?.userAddress || userAddress;
  if (!addressForDecrypt || typeof addressForDecrypt !== 'string' || !addressForDecrypt.trim()) {
    return null;
  }
  const chainKey = getChainKeyForTx(tx);
  return `${addressForDecrypt.toLowerCase().trim()}|${String(chainKey).trim()}`;
};

/**
 * Resolve active / queued ciphertext + IBE identity pairs for the viewing user's role.
 * Identities prefer stored `*RandomnessId` fields (address.toLowerCase() + chain pk);
 * fall back to legacy `address|chain` when Ids are absent (older grants).
 *
 * @param {Object} tx
 * @param {string} userAddress
 * @returns {{
 *   role: string,
 *   active: { ciphertext: string|null, identity: string|null, pepIdentity: string|null },
 *   queued: { ciphertext: string|null, identity: string|null, pepIdentity: string|null }
 * }}
 */
export const getEncryptedRandomnessForRole = (tx, userAddress) => {
  const role = getRoleForUser(tx, userAddress);
  const isSender = role === 'sender';
  const legacyIdentity = getLegacyIbeIdentity(tx, userAddress);

  const activeCiphertext = isSender
    ? tx.encryptedSenderRandomness
    : tx.encryptedReceiverRandomness;
  const activeIdentityStored = isSender
    ? tx.encryptedSenderRandomnessId
    : tx.encryptedReceiverRandomnessId;
  const queuedCiphertext = isSender
    ? tx.queuedEncryptedSenderRandomness
    : tx.queuedEncryptedReceiverRandomness;
  const queuedIdentityStored = isSender
    ? tx.queuedEncryptedSenderRandomnessId
    : tx.queuedEncryptedReceiverRandomnessId;

  const normalizeIdentity = (stored) =>
    typeof stored === 'string' && stored.trim() ? stored.trim() : null;

  return {
    role,
    active: {
      ciphertext: activeCiphertext || null,
      // PEP + IBE identity for the active ciphertext. Prefer stored Id; else legacy.
      identity: normalizeIdentity(activeIdentityStored) || (activeCiphertext ? legacyIdentity : null),
      // Address-only identity used by older PEP key requests when RandomnessId is missing.
      pepIdentity:
        normalizeIdentity(activeIdentityStored) ||
        (tx.userAddress || userAddress || null)
    },
    queued: {
      ciphertext: queuedCiphertext || null,
      identity: normalizeIdentity(queuedIdentityStored) || (queuedCiphertext ? legacyIdentity : null),
      pepIdentity:
        normalizeIdentity(queuedIdentityStored) ||
        (tx.userAddress || userAddress || null)
    }
  };
};

/**
 * Decrypt IBE randomness for a tx, trying active then queued with matching keys.
 *
 * When the chain pk rotates, active and queued ciphertexts use different identities
 * (`address + activePk` vs `address + queuedPk`). Callers supply keys via `getIbeKey`.
 *
 * @param {Object} tx
 * @param {string} userAddress
 * @param {(identity: string, meta: { which: 'active'|'queued', pepIdentity: string }) => Promise<string>} getIbeKey
 * @returns {Promise<{ decryptedRandomness: string, role: string, which: 'active'|'queued' }>}
 */
export const decryptIbeRandomnessForTransaction = async (tx, userAddress, getIbeKey) => {
  const { role, active, queued } = getEncryptedRandomnessForRole(tx, userAddress);
  if (!active.ciphertext && !queued.ciphertext) {
    throw new Error(`No encrypted randomness found for ${role} role`);
  }

  const tryPair = async (pair, which) => {
    if (!pair.ciphertext || !pair.identity) {
      throw new Error(`Missing ${which} ciphertext or identity for ${role}`);
    }
    const ibeKey = await getIbeKey(pair.identity, {
      which,
      pepIdentity: pair.pepIdentity || pair.identity
    });
    if (!ibeKey) {
      throw new Error(`No IBE key available for ${which} identity`);
    }
    console.log('[transactionDecryption] Decrypting IBE randomness', {
      role,
      which,
      identityPreview: `${String(pair.identity).substring(0, 20)}…`,
      txHash: tx.transactionHash
    });
    return decryptRandomness(pair.identity, pair.ciphertext, ibeKey);
  };

  if (active.ciphertext) {
    try {
      const decryptedRandomness = await tryPair(active, 'active');
      return { decryptedRandomness, role, which: 'active' };
    } catch (primaryError) {
      if (!queued.ciphertext) {
        throw primaryError;
      }
      console.warn(
        '[transactionDecryption] Active randomness decrypt failed; trying queued identity (pk may have changed):',
        primaryError?.message || primaryError
      );
      try {
        const decryptedRandomness = await tryPair(queued, 'queued');
        return { decryptedRandomness, role, which: 'queued' };
      } catch (queuedError) {
        throw new Error(
          `Failed to decrypt both primary and queued ${role} randomness: ${queuedError.message}`
        );
      }
    }
  }

  const decryptedRandomness = await tryPair(queued, 'queued');
  return { decryptedRandomness, role, which: 'queued' };
};

/**
 * Decrypt a single transaction amount.
 * @param {Object} tx - Transaction object (id, chainId, transactionHash, details.token, encrypted*Randomness, encrypted*RandomnessId, etc.)
 * @param {string} userAddress - The user address (viewing user)
 * @param {string|((identity: string, meta: object) => Promise<string>)} ibeKeyOrGetter - IBE private key, or async getter keyed by identity
 * @param {Object} wasmModule - { decrypt_ciphertext_with_randomness, decode_transfer_proof }
 * @param {{ onProgress?: (phase: string) => void }} [options]
 * @returns {Promise<{ displayAmount: string }>}
 */
export const decryptTransactionAmount = async (tx, userAddress, ibeKeyOrGetter, wasmModule, options = {}) => {
  const report = (phase) => {
    try {
      options.onProgress?.(phase);
    } catch {
      // ignore UI callback errors
    }
  };

  const addressForDecrypt = tx.userAddress || userAddress;
  if (!addressForDecrypt) {
    throw new Error('User address is required for decryption.');
  }

  const getIbeKey =
    typeof ibeKeyOrGetter === 'function'
      ? ibeKeyOrGetter
      : async () => ibeKeyOrGetter;

  report('ibe_randomness');
  const { decryptedRandomness, role } = await decryptIbeRandomnessForTransaction(
    tx,
    userAddress,
    getIbeKey
  );

  const chain = getChainForRequest(tx);
  if (!chain) throw new Error(`Chain with ID ${tx.chainId} not found`);
  const rpcUrl = getRpcUrlForTx(chain, tx);
  if (!rpcUrl) throw new Error(`RPC URL not configured for chain ${chain.id}`);

  report('rpc_fetch');
  console.log('[transactionDecryption] Fetching calldata from RPC', {
    chain: chain.id,
    rpcUrl,
    txHash: tx.transactionHash
  });

  const staticNet = staticNetworkForProvider(chain);
  // ConnectionInfo timeout fails unreachable RPCs (e.g. Stable) in ~20s instead of ~120s.
  const connection = { url: rpcUrl, timeout: RPC_FETCH_TIMEOUT_MS };
  const provider = staticNet
    ? new ethers.providers.StaticJsonRpcProvider(connection, staticNet)
    : new ethers.providers.StaticJsonRpcProvider(connection);

  let calldata;
  try {
    calldata = await getTransactionCalldata(provider, tx.transactionHash, {
      chainName: chain.name || chain.id,
      rpcUrl
    });
  } catch (fetchErr) {
    console.error('[transactionDecryption] Failed to fetch calldata via raw RPC:', fetchErr);
    throw fetchErr;
  }

  report('decode_proof');
  // Known confidential-transfer ABIs (newest first). Current diamond takes a trailing
  // Predicate Attestation; older deployments used `bool offchainZKP` only, and arb-fork
  // omitted the bool. Register all overloads so parseTransaction() matches the selector.
  const iface = new ethers.utils.Interface([
    'function transferConfidential(address recipient, address token, bytes proof, bool offchainZKP, (string uuid, uint256 expiration, address attester, bytes signature) attestation)',
    'function transferConfidential(address recipient, address token, bytes proof, bool useOffchainVerify)',
    'function transferConfidential(address recipient, address token, bytes proof)'
  ]);
  let decodedInput;
  try {
    decodedInput = iface.parseTransaction({ data: calldata });
  } catch (parseErr) {
    const selector = typeof calldata === 'string' ? calldata.slice(0, 10) : '?';
    throw new Error(
      `Transaction does not call a known transferConfidential ABI (selector ${selector}). ` +
      'Chain may use a different confidential transfer ABI.'
    );
  }
  if (!decodedInput) {
    const selector = typeof calldata === 'string' ? calldata.slice(0, 10) : '?';
    throw new Error(
      `Transaction does not call transferConfidential (selector ${selector}). ` +
      'Chain may use a different confidential transfer ABI.'
    );
  }
  const proofData =
    chain.id === 'tempo'
      ? await resolveTempoProofFromIpfs(decodedInput.args.proof)
      : proofArgToHex(decodedInput.args.proof);
  const decoded = ethers.utils.defaultAbiCoder.decode(['string', 'string', 'string'], proofData);
  const validityProofBase64 = decoded[1];
  const handleIndex = role === 'sender' ? 0 : 1;
  const proofDecodeResult = JSON.parse(
    wasmModule.decode_transfer_proof(validityProofBase64, handleIndex)
  );
  if (!proofDecodeResult.success) {
    throw new Error('Failed to decode validity proof');
  }

  report('elgamal');
  console.log('[transactionDecryption] Decrypting ElGamal ciphertext');
  const transferAmountCiphertext = proofDecodeResult.transfer_amount_ciphertext;
  const amountRaw = await decryptElGamalWithRandomness(
    wasmModule,
    transferAmountCiphertext,
    decryptedRandomness
  );

  const displayDecimals = 2;
  const amount = amountRaw / Math.pow(10, displayDecimals);
  let formattedAmount = amount.toFixed(displayDecimals);
  if (amount > 0 && amount < 1) {
    const parts = formattedAmount.split('.');
    if (parts.length === 2) {
      const decimalPart = parts[1].replace(/0+$/, '');
      formattedAmount = decimalPart ? `${parts[0]}.${decimalPart}` : parts[0];
    }
  } else {
    formattedAmount = formattedAmount.replace(/\.?0+$/, '');
  }

  const tokenSymbol = tx.details?.token || 'TOKEN';
  const displayAmount = `${formattedAmount} ${tokenSymbol}`;
  return { displayAmount };
};
